import { format } from "date-fns";
import { FiLoader } from "react-icons/fi";

/* =========================
   HELPERS
   ========================= */

const formatDateSafe = (value) => {
  if (!value) return "—";
  return format(new Date(value), "MMM dd, yyyy");
};

const formatTimeSafe = (time) => {
  if (!time) return "—";
  return format(new Date(`1970-01-01T${time}`), "hh:mm a");
};

const getImageSrc = (img) => {
  if (typeof img === "string") return img;
  return img?.preview || img?.url || "";
};

const getOverridesSummary = (availabilityMap = {}) => {
  const result = {
    FULL_CLOSE: [],
    CUSTOM_HOURS: [],
    CLOSURE_WINDOWS: [],
  };

  Object.entries(availabilityMap).forEach(([date, value]) => {
    if (value?.overrideType) {
      result[value.overrideType].push({ date, ...value });
    }
  });

  return result;
};

/* =========================
   COMPONENT
   ========================= */

export default function Step4Review({
  formData,
  onBack,
  onSubmit,
  isEditMode = false,
  isSubmitting = false,
}) {
  const {
    title,
    description,
    category,
    difficulty,
    price,
    importantNotes,
    images,
    addOns,
    status,
    schedules,
    escalationRules,
    baseDuration,
    baseGroupSize,
    bufferTime,
    calendarAvailability,
    isPrivate,
    maxGuestCount,
  } = formData || {};

  const overrideSummary = getOverridesSummary(calendarAvailability);
  const isApproved = status?.toLowerCase() === "approved";

  const handleSaveAsDraft = () => onSubmit?.("Draft");
  const handleSubmitForApproval = () => onSubmit?.("Pending");

  return (
    <>
      <div className="bg-white rounded-xl border shadow-sm p-4 sm:p-6 lg:p-8">
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_1.2fr] gap-6 lg:gap-8">
          {/* ================= LEFT SIDE ================= */}
          <div>
            <h3 className="text-[18px] font-semibold text-gray-900 mb-4">
              Experience Details
            </h3>

            <ul className="text-[15px] text-gray-800 space-y-3">
              <li>
                <b>Title:</b> {title || "—"}
              </li>
              <li>
                <b>Description:</b> {description || "—"}
              </li>
              <li>
                <b>Category:</b> {category || "—"}
              </li>
              <li>
                <b>Difficulty:</b> {difficulty || "—"}
              </li>
              <li>
                <b>Price:</b> ${price || 0}
              </li>

              <li>
                <b>Group Type:</b>{" "}
                {isPrivate ? "Private Experience" : "Public Experience"}
              </li>

              {!isPrivate && (
                <li>
                  <b>Max Guests:</b> {maxGuestCount || "—"}
                </li>
              )}

              <li>
                <b>Buffer Time:</b> {bufferTime || 0} minutes
              </li>

              {importantNotes && (
                <li>
                  <b>Important Notes:</b> {importantNotes}
                </li>
              )}

              {addOns?.length > 0 && (
                <li>
                  <b>Add-ons:</b>
                  <ul className="ml-4 mt-2 space-y-1">
                    {addOns.map((a, i) => (
                      <li key={i} className="text-sm text-gray-700">
                        • {a.name} — ${a.price}
                      </li>
                    ))}
                  </ul>
                </li>
              )}
            </ul>

            {/* ================= SCHEDULES ================= */}
            <div className="mt-8">
              <h4 className="text-[16px] font-semibold mb-3">Schedules</h4>

              {schedules?.length ? (
                <div className="space-y-3">
                  {schedules.map((s, i) => (
                    <div key={i} className="border rounded-xl p-4">
                      <div className="font-semibold text-sm mb-2">{s.name}</div>
                      <div className="text-sm text-gray-700">
                        Days: {s.days?.join(", ") || "—"}
                      </div>
                      <div className="text-sm text-gray-700">
                        Time:{" "}
                        {s.openTime && s.closeTime
                          ? `${formatTimeSafe(s.openTime)} – ${formatTimeSafe(
                              s.closeTime,
                            )}`
                          : "—"}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-gray-500">No schedules defined.</p>
              )}
            </div>

            {/* ================= ESCALATION RULES ================= */}
            <div className="mt-8">
              <h4 className="text-[16px] font-semibold mb-3">
                Escalation Rules
              </h4>

              {escalationRules?.length ? (
                <div className="border rounded-xl divide-y">
                  {escalationRules.map((r, i) => (
                    <div key={i} className="p-4">
                      <div className="font-semibold text-sm">
                        Guests: {r.minGuests} – {r.maxGuests}
                      </div>
                      <div className="text-sm text-gray-700">
                        Duration: {r.duration} minutes
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-gray-500">
                  No escalation rules defined.
                </p>
              )}
            </div>

            {/* ================= OVERRIDES ================= */}
            <div className="mt-8">
              <h4 className="text-[16px] font-semibold mb-4">
                Availability Overrides
              </h4>

              {Object.values(overrideSummary).every(
                (arr) => arr.length === 0,
              ) ? (
                <p className="text-sm text-gray-500">No overrides applied.</p>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                  {/* FULL DAY CLOSED */}
                  <div className="min-w-0">
                    <div className="text-red-600 font-semibold text-sm mb-2">
                      Full Day Closed
                    </div>

                    {overrideSummary.FULL_CLOSE.length === 0 ? (
                      <div className="text-sm text-gray-400">—</div>
                    ) : (
                      <div className="space-y-1 text-sm text-gray-700">
                        {overrideSummary.FULL_CLOSE.map((o) => (
                          <div key={o.date}>{formatDateSafe(o.date)}</div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* CUSTOM HOURS */}
                  <div className="min-w-0">
                    <div className="text-amber-600 font-semibold text-sm mb-2">
                      Custom Hours
                    </div>

                    {overrideSummary.CUSTOM_HOURS.length === 0 ? (
                      <div className="text-sm text-gray-400">—</div>
                    ) : (
                      <div className="space-y-1 text-sm text-gray-700">
                        {overrideSummary.CUSTOM_HOURS.map((o) => (
                          <div key={o.date}>
                            {formatDateSafe(o.date)} —{" "}
                            {formatTimeSafe(o.customHours?.start)} to{" "}
                            {formatTimeSafe(o.customHours?.end)}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* PARTIAL CLOSURES */}
                  <div className="min-w-0">
                    <div className="text-purple-600 font-semibold text-sm mb-2">
                      Partial Closures
                    </div>

                    {overrideSummary.CLOSURE_WINDOWS.length === 0 ? (
                      <div className="text-sm text-gray-400">—</div>
                    ) : (
                      <div className="space-y-1 text-sm text-gray-700">
                        {overrideSummary.CLOSURE_WINDOWS.map((o) => (
                          <div key={o.date}>
                            {formatDateSafe(o.date)} —{" "}
                            {o.closures?.map((c, i) => (
                              <span key={i}>
                                {formatTimeSafe(c.start)}–
                                {formatTimeSafe(c.end)}{" "}
                              </span>
                            ))}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* ================= RIGHT SIDE (UNCHANGED) ================= */}
          <div>
            {images?.length ? (
              <>
                <img
                  src={getImageSrc(images[0])}
                  alt=""
                  className="w-full h-[240px] object-cover rounded-xl"
                />

                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mt-4">
                  {images.slice(1).map((img, i) => (
                    <img
                      key={i}
                      src={getImageSrc(img)}
                      alt=""
                      className="h-[120px] w-full object-cover rounded-xl"
                    />
                  ))}
                </div>
              </>
            ) : (
              <div className="border rounded-xl p-6 text-center text-gray-500">
                No images uploaded
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ================= FOOTER ================= */}
      <div className="flex flex-col-reverse sm:flex-row justify-between gap-4 mt-6">
        <button
          type="button"
          onClick={onBack}
          disabled={isSubmitting}
          className="h-11 px-8 rounded-lg border border-[#0F2F62] text-[#0F2F62] font-semibold text-sm"
        >
          Back
        </button>

        <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-3 sm:gap-4 w-full">
          {/* SAVE AS DRAFT */}
          <button
            type="button"
            onClick={handleSaveAsDraft}
            disabled={isSubmitting}
            className="
      h-11
      w-full sm:w-auto
      px-6 sm:px-8
      rounded-lg
      border border-gray-400
      text-gray-700
      font-semibold text-sm
      transition
      hover:bg-gray-50
      disabled:opacity-50
    "
          >
            Save as Draft
          </button>

          {/* SUBMIT / UPDATE */}
          <button
            type="button"
            onClick={handleSubmitForApproval}
            disabled={isSubmitting}
            className="
      h-11
      w-full sm:w-auto
      px-6 sm:px-10
      rounded-lg
      bg-[#00B8C8]
      text-white
      font-semibold text-sm
      flex items-center justify-center gap-2
      transition
      hover:bg-[#00a5b4]
      disabled:opacity-50
    "
          >
            {isSubmitting && <FiLoader className="animate-spin" size={16} />}
            {isEditMode && isApproved
              ? "Update Experience"
              : "Submit for Approval"}
          </button>
        </div>
      </div>
    </>
  );
}
